import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';

export type Result = 'pass' | 'degraded' | 'fail';

export interface DiagnosticResult {
    /** Name of the check */
    name: string;
    /** Short description */
    description: string;
    /** Actual result */
    result: Result;
    /** Any error if applicable */
    error?: object;
    /** Any extra data from the result */
    data?: object;
}

interface CloudflareDnsResponse {
    // DNS RCodes https://www.iana.org/assignments/dns-parameters/dns-parameters.xhtml#dns-parameters-6
    Status: number;
    TC: boolean;
    RD: boolean;
    RA: boolean;
    AD: boolean;
    CD: boolean;
    Question: Array<{
        name: string;
        // DNS Record Type requested https://www.iana.org/assignments/dns-parameters/dns-parameters.xhtml#dns-parameters-4
        type: number;
    }>;
    Answer: Array<{
        name: string;
        type: number;
        TTL: number;
        data: string;
    }>;
}

const checkDns = async (rootDomain: string): Promise<DiagnosticResult> => {
    try {
        const nameToCheck = rootDomain === 'localhost' ? 'app.mews-develop.com' : rootDomain;

        const response = await window.fetch(`https://cloudflare-dns.com/dns-query?name=${nameToCheck}&type=A`, {
            method: 'GET',
            headers: {
                accept: 'application/dns-json',
            },
        });
        const dnsCheck = (await response.json()) as CloudflareDnsResponse;

        return {
            name: 'DNS',
            description: 'Checks if the browser can resolve names to IP Addresses',
            result: dnsCheck.Status === 0 ? 'pass' : 'fail',
        };
    } catch {
        return {
            name: 'DNS',
            description: 'Checks if the browser can resolve names to IP Addresses',
            result: 'fail',
        };
    }
};

const checkGeneralApi = async (): Promise<DiagnosticResult> => {
    try {
        const response = await window.fetch('/api/general/v1/healthCheck');
        const succeeded = response.status === 200;

        return {
            name: 'Mews General API',
            description: 'Checks if the browser can make HTTP calls to the General API',
            result: succeeded ? 'pass' : 'fail',
        };
    } catch {
        return {
            name: 'Mews General API',
            description: 'Checks if the browser can make HTTP calls to the General API',
            result: 'fail',
        };
    }
};

const checkHardware = async (): Promise<DiagnosticResult> => {
    try {
        // we safely access all these APIs, as they are not on FF / Safari, only for enrichment
        // if available

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const looseNav = window.navigator as any;

        return Promise.resolve({
            name: 'Hardware',
            description: 'Checks the hardware of the device',
            result: 'pass',
            data: {
                numCpus: looseNav.hardwareConcurrency ?? undefined,
                totalDeviceMemoryGb: looseNav.deviceMemory ?? undefined,
                connection: {
                    effectiveType: looseNav?.connection?.effectiveType ?? undefined,
                    downlinkMbs: looseNav?.connection?.downlink ?? undefined,
                    saveData: looseNav?.connection?.saveData ?? undefined,
                },
            },
        });
    } catch {
        return Promise.resolve({
            name: 'Hardware',
            description: 'Checks the hardware of the device',
            result: 'degraded',
            data: {
                memory: undefined,
                numCpus: undefined,
                totalDeviceMemoryGb: undefined,
                connection: {
                    effectiveType: undefined,
                    downlink: undefined,
                    saveData: undefined,
                },
            },
        });
    }
};

interface NetworkCheckConfig {
    /** The URL to extract the domain from for use in the DNS check */
    urlToProbe: string;
}

/** Runs a network check to verify if the browser client can:
 * - resolve DNS
 * - TLS handshake
 * - make HTTP calls
 * - receive data
 * - connect to mews services
 */
const networkCheck = async (config: NetworkCheckConfig): Promise<Array<DiagnosticResult>> => {
    const results: Array<DiagnosticResult> = [];

    const rootDomain = new URL(config.urlToProbe).hostname;

    const pushIntoResults = (result: DiagnosticResult) => {
        results.push(result);
    };

    const promises = [
        checkDns(rootDomain).then(pushIntoResults),
        checkGeneralApi().then(pushIntoResults),
        checkHardware().then(pushIntoResults),
    ];

    await Promise.allSettled(promises);

    return results;
};

/** Runs a network check and logs the results */
export const runDiagnosticCheck = async (config: NetworkCheckConfig) => {
    const results = await networkCheck(config);

    const allResultsFailed = results.filter(({ name }) => name !== 'Hardware').every(({ result }) => result === 'fail');

    // TODO default severity should be Debug after test is OK!
    const severity = allResultsFailed ? CoralogixLogSeverity.Critical : CoralogixLogSeverity.Debug;

    CoralogixRum.log(
        severity,
        'Network check results',
        {
            results,
        },
        {
            'mews.dx.network': 'results',
        }
    );
};
